import {
  IoAdd,
  IoArrowBack,
  IoArrowDown,
  IoArrowForward,
  IoArrowUp,
  IoEllipsisHorizontal,
  IoExit,
  IoHome,
  IoMenu,
  IoPause,
  IoPlay,
  IoPlayBack,
  IoPlayForward,
  IoPower,
  IoRadioButtonOn,
  IoRemove,
  IoReturnDownBack,
  IoSquare,
  IoVolumeOff,
} from "react-icons/io5";
import { FaEject } from "react-icons/fa";
import { REMOTE_TYPES as R } from "../../constants/devices";

// Keys of the app's remotes (app/screens/HashhRemote/Layouts/*.js) by the names
// in the remote's key map: a key's IR code is keys[name], as the app's
// checkKeyAndReturn reads it. Icons are the Ionicons the app draws (FontAwesome
// for Eject); tone colours the icon like the app's alertError/alertSuccess.
export const KEYS = Object.freeze({
  POWER: { label: "Power", icon: IoPower, tone: "danger" },
  INPUT: { label: "Input", icon: IoExit },
  MENU: { label: "Menu", icon: IoMenu },
  HOME: { label: "Home", icon: IoHome },
  BACK: { label: "Back", icon: IoReturnDownBack },
  EJECT: { label: "Eject", icon: FaEject },
  MUTE: { label: "Mute", icon: IoVolumeOff },
  REW: { label: "Rewind", icon: IoPlayBack },
  PLAY: { label: "Play", icon: IoPlay },
  FF: { label: "Fast forward", icon: IoPlayForward },
  REC: { label: "Record", icon: IoRadioButtonOn, tone: "danger" },
  STOP: { label: "Stop", icon: IoSquare },
  PAUSE: { label: "Pause", icon: IoPause },
  UP: { label: "Up", icon: IoArrowUp },
  RIGHT: { label: "Right", icon: IoArrowForward },
  DOWN: { label: "Down", icon: IoArrowDown },
  LEFT: { label: "Left", icon: IoArrowBack },
  OK: { label: "OK" },
  "CH+": { label: "Channel up", icon: IoArrowUp },
  "CH-": { label: "Channel down", icon: IoArrowDown },
  "VOL+": { label: "Volume up", icon: IoAdd },
  "VOL-": { label: "Volume down", icon: IoRemove },
  "ZOOM+": { label: "Zoom in", icon: IoAdd },
  "ZOOM-": { label: "Zoom out", icon: IoRemove },
  ON: { label: "Power on", icon: IoPower, tone: "success" },
  OFF: { label: "Power off", icon: IoPower, tone: "danger" },
});

// Direction pad keys in tab order (clockwise); OK sits below the pad.
export const DPAD = Object.freeze(["UP", "RIGHT", "DOWN", "LEFT"]);

// The app's number panel: 1-9, then 0 between two empty places.
export const NUMBER_PAD = Object.freeze([
  "NUM_1", "NUM_2", "NUM_3", "NUM_4", "NUM_5", "NUM_6", "NUM_7", "NUM_8", "NUM_9", null, "NUM_0", null,
]);
export const NUMBER_KEYS = Object.freeze(NUMBER_PAD.filter(Boolean));

// The AC remote steps through 16-30 °C and sends TEMP_<degrees>.
export const AC_TEMPS = Object.freeze(Array.from({ length: 15 }, (_, index) => 16 + index));
export const tempKey = (degrees) => `TEMP_${degrees}`;

const key = (name, icon) => ({ kind: "key", name, icon });
const rocker = (label, groupLabel, up, down) => ({ kind: "rocker", label, groupLabel, keys: [up, down] });
const NUMBERS = { kind: "numbers" };
const DIRECTIONS = { kind: "dpad" };
// Keeps the direction pad centred where the app leaves an empty 60px column.
const SPACER = { kind: "spacer" };

const CHANNEL = rocker("CH", "Channel", "CH+", "CH-");
const VOLUME = rocker("VOL", "Volume", "VOL+", "VOL-");
const ZOOM = rocker("ZOOM", "Zoom", "ZOOM+", "ZOOM-");

// Menu appears twice on the app's remotes: once with the menu icon and again
// with the ellipsis next to Mute. Both send MENU.
const MENU_MUTE = [key("MENU", IoEllipsisHorizontal), key("MUTE")];
const PLAYBACK = [key("REW"), key("PLAY"), key("FF")];
const RECORDING = [key("REC"), key("STOP"), key("PAUSE")];

const TV_ROWS = [
  [key("POWER"), key("INPUT"), key("MENU")],
  [key("HOME"), NUMBERS, key("BACK")],
  [CHANNEL, DIRECTIONS, VOLUME],
  MENU_MUTE,
  PLAYBACK,
  RECORDING,
];

// Rows of keys for each remote type, top to bottom. The AC remote has its own
// layout (AcRemote.js).
export const LAYOUTS = Object.freeze({
  [R.TV]: TV_ROWS,
  [R.STB]: TV_ROWS,
  [R.DVD]: [
    [key("POWER"), key("EJECT"), key("MENU")],
    [key("HOME"), NUMBERS, key("BACK")],
    [SPACER, DIRECTIONS, VOLUME],
    MENU_MUTE,
    PLAYBACK,
    RECORDING,
  ],
  [R.STEREO]: [
    [key("POWER"), key("INPUT"), key("MENU")],
    [key("HOME"), key("BACK")],
    [SPACER, DIRECTIONS, VOLUME],
    MENU_MUTE,
    PLAYBACK,
    RECORDING,
  ],
  [R.PROJECTOR]: [
    [key("POWER"), key("INPUT"), key("MENU")],
    [key("HOME"), key("BACK")],
    [ZOOM, DIRECTIONS, VOLUME],
    MENU_MUTE,
    PLAYBACK,
  ],
});

const namesOf = (item) => {
  switch (item.kind) {
    case "key":
      return [item.name];
    case "rocker":
      return item.keys;
    case "dpad":
      return [...DPAD, "OK"];
    case "numbers":
      return NUMBER_KEYS;
    default:
      return [];
  }
};

// Whether the remote shows any key dimmed because its code isn't learned.
export const hasDimmedKeys = (type, isLearned) => {
  if (type === R.AC) {
    return !isLearned("ON") || !isLearned("OFF") || !AC_TEMPS.some((degrees) => isLearned(tempKey(degrees)));
  }
  return (LAYOUTS[type] ?? []).some((row) => row.some((item) => namesOf(item).some((name) => !isLearned(name))));
};
